import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import {
	type State,
	type Language,
	type LanguagesUpdatePayload,
	type UpdateLanguagesParams,
	LANGUAGE_OPERATIONS,
} from './types.tsx';

type ParamsWithLanguageLabel = UpdateLanguagesParams & {
	languageLabel: string;
};

const getDefaultLanguageLabel = (languages: Language[], languageKey: string): string => {
	const language = languages.find((l) => l.key === languageKey);
	return language ? language.label : '';
};

const fetchLanguages =
	(projectId: number, analyticsEvent: UIAnalyticsEvent): Action<State> =>
	async ({ setState }) => {
		setState({ loading: true });
		try {
			const {
				languages,
				projectDefaultLanguageKey: key,
				shouldAskUserToConfirmLanguages,
			} = await fetchJson(`/rest/servicedesk/lingo-integration/1/manage-language/${projectId}`);
			setState({
				data: {
					languages,
					projectDefaultLanguage: { key, label: getDefaultLanguageLabel(languages, key) },
					shouldAskUserToConfirmLanguages,
				},
				loading: false,
			});
			fireOperationalAnalytics(analyticsEvent, 'languages fetched', { projectId });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({ error, loading: false });
			trackFetchErrors({
				id: 'services.languages.fetch',
				packageName: 'jiraServicedeskLanguages',
				error,
			});
		}
	};

const getUpdatePayload = (
	defaultLanguageKey: string,
	updatedLanguages: Language[],
): LanguagesUpdatePayload[] => {
	const defaultLanguage = { languageTag: defaultLanguageKey, enabled: true };
	return [
		defaultLanguage,
		...updatedLanguages
			.filter((l) => l.state !== 'unused')
			.filter((l) => l.key !== defaultLanguageKey)
			.map((l) => ({ languageTag: l.key, enabled: l.state === 'active' })),
	];
};

// A generic function to call the update languages endpoint.
// This is used by all operations: adding, removing, toggling availability and setting default.
const updateLanguages =
	// @ts-expect-error - TS2304 - Cannot find name 'SetState'.


		(setState: SetState<State>) =>
		async ({
			projectId,
			updatedLanguages,
			defaultLanguageKey,
			analytics,
		}: UpdateLanguagesParams) => {
			setState({ loading: true });
			try {
				const {
					languages,
					projectDefaultLanguageKey: key,
					shouldAskUserToConfirmLanguages,
				} = await performPostRequest(
					`/rest/servicedesk/lingo-integration/1/manage-language/${projectId}`,
					{
						body: JSON.stringify({
							projectDefaultLanguageKey: defaultLanguageKey,
							languages: getUpdatePayload(defaultLanguageKey, updatedLanguages),
						}),
					},
				);
				setState({
					error: undefined,
					data: {
						languages,
						projectDefaultLanguage: {
							key,
							label: getDefaultLanguageLabel(languages, key),
						},
						shouldAskUserToConfirmLanguages,
					},
					loading: false,
				});
				if (analytics) {
					fireOperationalAnalytics(analytics.event, 'languages updated', { projectId });
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState({ error, loading: false });
				trackFetchErrors({
					id: 'services.languages.update',
					packageName: 'jiraServicedeskLanguages',
					error,
				});
			}
		};

const clearLanguageOperation =
	(): Action<State> =>
	async ({ setState }) => {
		setState({
			currentOperation: {
				type: LANGUAGE_OPERATIONS.NO_OP,
				updatedLanguages: [],
				addedLanguages: [],
				languageLabel: '',
				analytics: undefined,
			},
		});
	};

const setDefaultLanguage =
	(params: UpdateLanguagesParams): Action<State> =>
	async ({ setState }) => {
		await updateLanguages(setState)(params);
		const { analytics, updatedLanguages, addedLanguages } = params;
		setState({
			currentOperation: {
				type: LANGUAGE_OPERATIONS.SET_DEFAULT_LANGUAGE,
				updatedLanguages,
				addedLanguages,
				languageLabel: '',
				analytics,
			},
		});
	};

const addLanguages =
	(params: UpdateLanguagesParams): Action<State> =>
	async ({ setState }) => {
		await updateLanguages(setState)(params);
		const { analytics, updatedLanguages, addedLanguages } = params;
		setState({
			currentOperation: {
				type: LANGUAGE_OPERATIONS.ADD_LANGUAGE,
				updatedLanguages,
				addedLanguages,
				languageLabel: '',
				analytics,
			},
		});
	};

const removeLanguage =
	({ languageLabel, ...params }: ParamsWithLanguageLabel): Action<State> =>
	async ({ setState }) => {
		await updateLanguages(setState)(params);
		const { analytics, updatedLanguages, addedLanguages } = params;
		setState({
			currentOperation: {
				type: LANGUAGE_OPERATIONS.REMOVE_LANGUAGE,
				updatedLanguages,
				addedLanguages,
				languageLabel,
				analytics,
			},
		});
	};

const setAvailableLanguage =
	({ languageLabel, ...params }: ParamsWithLanguageLabel): Action<State> =>
	async ({ setState }) => {
		await updateLanguages(setState)(params);
		const { analytics, updatedLanguages, addedLanguages } = params;
		setState({
			currentOperation: {
				type: LANGUAGE_OPERATIONS.SET_AVAILABLE,
				updatedLanguages,
				addedLanguages,
				languageLabel,
				analytics,
			},
		});
	};

const setUnavailableLanguage =
	({ languageLabel, ...params }: ParamsWithLanguageLabel): Action<State> =>
	async ({ setState }) => {
		await updateLanguages(setState)(params);
		const { analytics, updatedLanguages, addedLanguages } = params;
		setState({
			currentOperation: {
				type: LANGUAGE_OPERATIONS.SET_UNAVAILABLE,
				updatedLanguages,
				addedLanguages,
				languageLabel,
				analytics,
			},
		});
	};

export const actions = {
	fetchLanguages,
	clearLanguageOperation,
	addLanguages,
	removeLanguage,
	setAvailableLanguage,
	setUnavailableLanguage,
	setDefaultLanguage,
} as const;

export type Actions = typeof actions;
